import React, { MouseEvent, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Document, PasswordResponses, pdfjs } from "react-pdf";

import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import { Button, Paper, Stack, Typography } from "@mui/material";
import { usePrompt } from "hooks";
import { IDataExtractionTag, Nullable, Polygons, Size, Word } from "store/types";
import { defineNumberFormat, separatedStringToNumber } from "utils/strings";

import { PdfPasswordPrompt } from "../components/PdfPasswordPrompt";
import { usePDFPageVisibility } from "../hooks/usePDFPageVisibility";
import { PDFPageContainer } from "./PDFPageContainer";
import { PDFToolbar } from "./PDFToolbar";
import { PolygonParameterPopover } from "./PolygonParameterPopover";

pdfjs.GlobalWorkerOptions.workerSrc = "/pdf.worker.min.mjs";

interface Props {
  file: File;
  polygons: Nullable<Polygons>;
  polygonsLoading?: boolean;
  extractionTags: IDataExtractionTag[];
  onSelectParameter: (data: { measureId: number; filePolygonId: string; value: number; tagId?: number }) => void;
  containerWidth: number;
  showPolygons?: boolean;
  hoveredPolygonId: string | null;
  onHoverPolygon: (polygonId: string | null) => void;
  cachedPassword?: string;
  onPasswordVerified?: (password: string) => void;
}

export const DataExtractionPDFReader: React.FC<Props> = React.memo(
  ({
    file,
    polygons,
    polygonsLoading,
    extractionTags,
    onSelectParameter,
    containerWidth,
    showPolygons,
    hoveredPolygonId,
    onHoverPolygon,
    cachedPassword,
    onPasswordVerified
  }) => {
    const [numPages, setNumPages] = useState(0);
    const [pageSizes, setPageSizes] = useState<Map<number, Size>>(new Map());
    const [userScale, setUserScale] = useState(1);

    const scrollContainerRef = useRef<HTMLDivElement>(null);

    const { visiblePages, currentPage, pageRefs } = usePDFPageVisibility(scrollContainerRef, numPages);

    const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
    const [selectedWord, setSelectedWord] = useState<Word | null>(null);
    const [selectedValue, setSelectedValue] = useState<number>(0);

    const [locked, setLocked] = useState(false);
    const openPrompt = usePrompt();
    // Password the worker is currently trying; promoted to the cache once the document loads.
    const pendingPasswordRef = useRef<string | null>(null);

    const onDocumentLoadSuccess = useCallback(
      ({ numPages }: { numPages: number }) => {
        setNumPages(numPages);
        if (pendingPasswordRef.current) {
          onPasswordVerified?.(pendingPasswordRef.current);
          pendingPasswordRef.current = null;
        }
      },
      [onPasswordVerified]
    );

    const handlePassword = useCallback(
      async (callback: (password: string | null) => void, reason: number) => {
        // Reuse a password already accepted for this file — don't ask again on every switch.
        if (reason === PasswordResponses.NEED_PASSWORD && cachedPassword) {
          pendingPasswordRef.current = cachedPassword;
          callback(cachedPassword);
          return;
        }
        const password = await openPrompt({
          Component: PdfPasswordPrompt,
          props: { incorrect: reason === PasswordResponses.INCORRECT_PASSWORD }
        });
        if (password) {
          pendingPasswordRef.current = password;
          callback(password);
        } else {
          setLocked(true);
        }
      },
      [openPrompt, cachedPassword]
    );

    // Reset the password flow whenever a different file is loaded.
    useEffect(() => {
      setLocked(false);
      pendingPasswordRef.current = null;
    }, [file]);

    const handlePageSizeResolved = useCallback((pageNumber: number, size: Size) => {
      setPageSizes((prev) => {
        if (prev.get(pageNumber)?.width === size.width && prev.get(pageNumber)?.height === size.height) {
          return prev;
        }
        const next = new Map(prev);
        next.set(pageNumber, size);
        return next;
      });
    }, []);

    const getOriginalScale = useCallback(
      (pageNumber: number) => {
        const size = pageSizes.get(pageNumber);
        return size ? containerWidth / size.width : 1;
      },
      [pageSizes, containerWidth]
    );

    const scrollToPage = useCallback((page: number) => {
      const el = document.querySelector(`[data-page="${page}"]`);
      el?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, []);

    const closePopover = () => {
      setAnchorEl(null);
      setSelectedWord(null);
    };

    const handlePolygonClick = useCallback((e: MouseEvent<HTMLDivElement>, rectId: string, text: string) => {
      e.preventDefault();
      const numericValue = separatedStringToNumber(text, defineNumberFormat(text)) || 1;

      setSelectedWord({ rectId, text });
      setSelectedValue(numericValue);
      setAnchorEl(e.currentTarget);
    }, []);

    const highlightedPolygonIds = useMemo(() => new Set(extractionTags.map((tag) => tag.polygon_id)), [extractionTags]);

    const polygonTagNumber = useMemo(
      () =>
        extractionTags.reduce<Record<string, number>>((acc, tag, index) => {
          acc[tag.polygon_id] = index + 1;
          return acc;
        }, {}),
      [extractionTags]
    );

    const savedPolygonIds = useMemo(
      () => new Set(extractionTags.filter((tag) => !!tag.measure_data_id).map((tag) => tag.polygon_id)),
      [extractionTags]
    );

    const polygonTagMap = useMemo(
      () =>
        extractionTags.reduce<Record<string, { tagId: number; measureId: number; measureName: string }>>((acc, tag) => {
          if (tag.measure) {
            acc[tag.polygon_id] = {
              tagId: tag.id,
              measureId: tag.measure.id,
              measureName: tag.measure.name
            };
          }
          return acc;
        }, {}),
      [extractionTags]
    );

    const selectedPolygonTag = selectedWord ? (polygonTagMap[selectedWord.rectId] ?? null) : null;

    const handleApply = useCallback(
      (measureId: number, value: number) => {
        if (!selectedWord) return;
        onSelectParameter({
          measureId,
          filePolygonId: selectedWord.rectId,
          value,
          tagId: selectedPolygonTag?.tagId
        });
        closePopover();
      },
      [selectedWord, onSelectParameter, selectedPolygonTag]
    );

    const filememo = useMemo(() => file, [file]);
    const options = useMemo(() => ({ normalizeWhitespace: true }), []);

    return (
      <Paper
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          minWidth: "100%",
          width: "100% !important",
          borderRadius: "3px",
          boxShadow: "none",
          position: "relative"
        }}
      >
        <PDFToolbar
          zoomPercent={Math.round(userScale * 100)}
          pageNumber={currentPage}
          numPages={numPages}
          loading={polygonsLoading}
          onZoomChange={setUserScale}
          onZoomIn={() => setUserScale((s) => Math.min(3, +(s + 0.25).toFixed(2)))}
          onZoomOut={() => setUserScale((s) => Math.max(0.25, +(s - 0.25).toFixed(2)))}
          onPageChange={scrollToPage}
        />
        <Stack
          ref={scrollContainerRef}
          sx={{
            flex: 1,
            minHeight: 0,
            overflow: "auto",
            bgcolor: polygonsLoading ? "white" : "#f7f8fc",
            "& .react-pdf__Document": {
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              gap: "10px"
            }
          }}
        >
          {locked ? (
            <Stack sx={{ flex: 1, alignItems: "center", justifyContent: "center", gap: "12px", p: "24px" }}>
              <LockOutlinedIcon sx={{ fontSize: 40, color: "#7b92a9" }} />
              <Typography sx={{ fontSize: "14px", color: "#496682", textAlign: "center" }}>
                This document is password protected.
              </Typography>
              <Button
                onClick={() => setLocked(false)}
                sx={{
                  borderRadius: "32px",
                  bgcolor: "#071f35",
                  color: "white",
                  textTransform: "none",
                  px: "16px",
                  py: "4px",
                  fontSize: "16px",
                  fontWeight: 400,
                  lineHeight: "24px",
                  "&:hover": { bgcolor: "#0a2a45" }
                }}
              >
                Enter password
              </Button>
            </Stack>
          ) : (
            <Document
              file={filememo}
              options={options}
              renderMode="canvas"
              onLoadSuccess={onDocumentLoadSuccess}
              onPassword={handlePassword}
            >
              {Array.from({ length: numPages }, (_, i) => i + 1).map((pageNum) => (
                <PDFPageContainer
                  key={pageNum}
                  ref={pageRefs(pageNum)}
                  pageNumber={pageNum}
                  pageSize={pageSizes.get(pageNum)}
                  originalScale={getOriginalScale(pageNum)}
                  userScale={userScale}
                  isVisible={visiblePages.has(pageNum)}
                  polygons={polygons?.[pageNum]}
                  showPolygons={!!showPolygons}
                  highlightedPolygonIds={highlightedPolygonIds}
                  polygonTagNumber={polygonTagNumber}
                  savedPolygonIds={savedPolygonIds}
                  selectedWord={selectedWord}
                  hoveredPolygonId={hoveredPolygonId}
                  onPolygonClick={handlePolygonClick}
                  onHoverPolygon={onHoverPolygon}
                  onPageSizeResolved={handlePageSizeResolved}
                />
              ))}
            </Document>
          )}
        </Stack>

        {anchorEl && selectedWord && (
          <PolygonParameterPopover
            anchorEl={anchorEl}
            value={selectedValue}
            preselectedMeasure={
              selectedPolygonTag ? { id: selectedPolygonTag.measureId, name: selectedPolygonTag.measureName } : null
            }
            onApply={handleApply}
            onCancel={closePopover}
          />
        )}
      </Paper>
    );
  }
);
